import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackHeapEvent } from 'utils/analytics';
import ConfigureAvailability1 from './Step1';
import ConfigureAvailability2 from './Step2';
import ConfigureAvailability3 from './Step3';
import { Button, message } from 'antd';
import s from './s.module.less';
import { getNextStep, getPrevStep } from 'pages/PPAOnboardingSteps/utils';
import { EPPAStep } from 'types/ppaOnboarding';
import { saveStep } from 'api/ppaOnboarding';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import { IReviewData } from 'hooks/usePPAOnboardingReviewInfo';
import ConsentFormsStep, { ConsentFormsStepHandles } from 'pages/PaymentSetup/components/ConsentFormsStep';

interface IProps {
    setShowProgress: (show: boolean) => void;
    step?: EPPAStep;
    setStep: (step: EPPAStep) => void;
    reviewData: IReviewData;
    refetchReviewData?: () => void;
}

const formIds = {
    [EPPAStep.STEP_15]: 'workingHour',
    [EPPAStep.STEP_16]: 'notice',
};

const ConfigureAvailability = ({ setShowProgress, step, setStep, reviewData, refetchReviewData }: IProps) => {
    const navigate = useNavigate();
    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    const [formId, setFormId] = useState<string>();
    const [submitLoading, setSubmitLoading] = useState(false);
    const consentFormsStepRef = useRef<ConsentFormsStepHandles>(null);

    useEffect(() => {
        setFormId(formIds[step as keyof typeof formIds]);
    }, [step]);

    useEffect(() => {
        setShowProgress(true);
    }, []);

    const handleBack = useCallback(() => {
        if (step) {
            trackHeapEvent('WebApp - PPA Onboarding - ConfigureAvailability - Click - Back', {
                step,
            });
            const newStep = getPrevStep(step);
            setStep(newStep);
        } else {
            console.error('step is undefined');
        }
    }, [setStep, step]);

    const handleSaveStep = useCallback(async () => {
        if (step) {
            try {
                setSubmitLoading(true);
                const nextStep = getNextStep(step);
                const res = await saveStep(nextStep);
                if (res && !res.error) {
                    trackHeapEvent('WebApp - PPA Onboarding - ConfigureAvailability - Click - Next Success', {
                        fromStep: step,
                        toStep: nextStep,
                    });

                    if (step === EPPAStep.STEP_17) {
                        trackHeapEvent(
                            'WebApp - PPA Onboarding Steps - View - Complete Redirect',
                            {
                                fromStep: step,
                                toStep: nextStep,
                                toPage: 'review',
                            },
                        );
                        navigate(PATH.PPA_ONBOARDING_REVIEW);
                    } else {
                        setStep(nextStep);
                    }
                    refetchReviewData?.();
                } else {
                    trackHeapEvent('WebApp - PPA Onboarding - ConfigureAvailability - View - Next Failed', {
                        step,
                        error: res?.error,
                    });
                    message.error(res?.error);
                }
            } catch (error) {
                trackHeapEvent('WebApp - PPA Onboarding - ConfigureAvailability - View - Next Error', {
                    step,
                    error: error instanceof Error ? error.message : 'Unknown error',
                });
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        } else {
            console.error('step is undefined');
        }
    }, [setStep, step, navigate, refetchReviewData]);

    const commonProps = useMemo(() => {
        return {
            setNextButtonDisabled,
        };
    }, []);

    const handleConsentFormsSubmit = useCallback(async () => {
        try {
            setSubmitLoading(true);
            await consentFormsStepRef.current?.onSubmit();
        } catch (error) {
            console.error('Consent forms submit failed:', error);
            setSubmitLoading(false);
        }
    }, []);

    const contentRender = useMemo(() => {
        let content = null;

        if (step === EPPAStep.STEP_14) {
            content = <ConfigureAvailability1 />;
        } else if (step === EPPAStep.STEP_15) {
            content = <ConfigureAvailability2 {...commonProps} formId={formId as string} onSaveStep={handleSaveStep} />;
        } else if (step === EPPAStep.STEP_16) {
            content = <ConfigureAvailability3 {...commonProps} initValue={reviewData.calendarSetting} onSaveStep={handleSaveStep} formId={formId as string} />;
        } else if (step === EPPAStep.STEP_17) {
            content = <ConsentFormsStep ref={consentFormsStepRef} type="onboarding" isEdit isReview={false} onSaveStep={handleSaveStep} />;
        }

        return content;
    }, [commonProps, step, formId, handleSaveStep, reviewData]);

    const footerBtnsRender = useMemo(() => {
        if (step === EPPAStep.STEP_14) {
            return (
                <>
                    <Button onClick={() => {
                        setStep(getPrevStep(step as EPPAStep));
                    }}
                    >Back
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            handleSaveStep();
                        }}
                    >Next
                    </Button>
                </>
            );
        } else if (step === EPPAStep.STEP_17 || step === EPPAStep.STEP_17_1) {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button type="primary" loading={submitLoading} onClick={handleConsentFormsSubmit} disabled={nextButtonDisabled}>Next</Button>
                </>
            );
        } else {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button type="primary" loading={submitLoading} htmlType="submit" form={formId} disabled={nextButtonDisabled}>Next</Button>
                </>
            );
        }
    }, [step, setStep, handleSaveStep, handleBack, formId, nextButtonDisabled, submitLoading, handleConsentFormsSubmit]);
    return (
        <div className={s.wrap}>
            {contentRender}
            <div className={s.footer}>
                {
                    footerBtnsRender
                }
            </div>
        </div>
    );
};

export default ConfigureAvailability;
